// RCPT1-S3-F1 (docs/build-briefs/RCPT1-S3-F1-remaining-tip-second-tap.md; DEC-489; PRD P0.9). The pure parts of the Remaining tip sheet: what a
// manager-approved tip above the limit leaves to collect, and the words and buttons for each moment of collecting it with a second card tap.
// No DOM, no state, so a unit test proves each rule directly.
//
// NO MONEY IS DECIDED HERE. The server charges exactly the remainder it recorded on the override; the figure below is that number as the server
// gave it, shown back. Nothing on this sheet is typed.

import { formatCents } from "./money.ts";
import { failedPaymentText } from "./terminalReaderDisplay.ts";

/** The one reason the floor records with an over-limit override: the reason list is not a typed field on the floor (PROVISIONAL). */
export const OVER_CAP_OVERRIDE_REASON = "Guest wrote a tip above the limit";

export type RemainderPhase =
  /** Nothing tried yet, or the last try failed and a new one may be sent. */
  | "ready"
  /** A charge is on the reader waiting for the guest's card. */
  | "waiting"
  /** The last try did not go through (declined, canceled on the reader, a reader problem, a refusal that charged nothing). */
  | "failed"
  /** The answer is not known. Nothing more may be charged until a manager checks. */
  | "pending"
  | "collected"
  /** Recorded as not collected (Cancel or Guest has left). */
  | "left";

export interface RemainderSheetInput {
  remainderCents: number;
  phase: RemainderPhase;
  /** The server's reason for a failed try, when it gave one. */
  failureKind?: string | null;
  /** A plain sentence for a failure that has no kind (a refusal before the reader was reached). Wins over the kind. */
  failureText?: string | null;
  /** Cancel was tapped and the stop is being sent. */
  stopping?: boolean;
  /** Why it was left, for the closing line. */
  leaveReason?: "guest_left" | "cancelled" | null;
}

export interface RemainderSheetView {
  title: string;
  /** "Rest of the tip: $30.00", always, so the figure never leaves the screen. */
  amountLine: string;
  message: string;
  tone: "normal" | "success" | "warning" | "danger";
  /** Null hides the button. */
  collectLabel: string | null;
  collectEnabled: boolean;
  cancelLabel: string | null;
  cancelEnabled: boolean;
  leaveLabel: string | null;
  doneLabel: string | null;
}

export function remainderSheetView(input: RemainderSheetInput): RemainderSheetView {
  const money = formatCents(input.remainderCents);
  const base = {
    title: "Remaining tip",
    amountLine: `Rest of the tip: ${money}`,
    collectLabel: null,
    collectEnabled: false,
    cancelLabel: null,
    cancelEnabled: false,
    leaveLabel: null,
    doneLabel: null,
  };
  switch (input.phase) {
    case "ready":
      return {
        ...base,
        message: "Ask the guest to tap or insert a card again.",
        tone: "normal",
        collectLabel: `Collect remaining tip ${money}`,
        collectEnabled: true,
        leaveLabel: "Guest has left",
      };
    case "waiting":
      return {
        ...base,
        message: input.stopping === true ? "Stopping the reader." : "Waiting for the guest's card on the reader.",
        tone: "normal",
        collectLabel: "Waiting for card",
        collectEnabled: false,
        cancelLabel: "Cancel",
        cancelEnabled: input.stopping !== true,
      };
    case "failed":
      return {
        ...base,
        message: input.failureText ?? failedPaymentText(input.failureKind),
        tone: "danger",
        collectLabel: "Try again",
        collectEnabled: true,
        leaveLabel: "Guest has left",
      };
    case "pending":
      return {
        ...base,
        message: "Not confirmed yet. Ask a manager to check the reader before charging again.",
        tone: "warning",
        doneLabel: "Close",
      };
    case "collected":
      return {
        ...base,
        message: `Collected ${money}. Card approved.`,
        tone: "success",
        doneLabel: "Done",
      };
    case "left":
      return {
        ...base,
        message:
          input.leaveReason === "cancelled"
            ? `Cancelled. ${money} was not collected.`
            : `${money} was not collected. The guest had left.`,
        tone: "normal",
        doneLabel: "Done",
      };
  }
}

/** What a collect answer means for the sheet. Only a Captured attempt is collected; Failed may be tried again; anything else is unknown. */
export function phaseFromAttempt(status: string): "collected" | "failed" | "pending" {
  if (status === "Captured") return "collected";
  if (status === "Failed") return "failed";
  return "pending";
}

/** The phase for a refusal code from the collect or leave routes. Null means a refusal that charged nothing and left the sheet as it was. */
export function phaseFromRefusal(code: string | undefined): RemainderPhase | null {
  switch (code) {
    case "remainder_already_collected":
      return "collected";
    case "remainder_left_uncollected":
      return "left";
    case "remainder_in_progress":
    case "payment_pending_reconciliation":
      return "pending";
    default:
      return null;
  }
}
